import Button from "@edaz/edazkit/Button/index";
import Icon, { IconType } from "@edaz/edazkit/Icon";
import Loader from "@edaz/edazkit/Loader/index";
import classNames from "classnames";
import React from "react";
import type { ViewComponent } from "@frui.ts/views";
import { preventDefault, registerView } from "@frui.ts/views";
import { observer } from "mobx-react-lite";
import { ButtonToolbar, Modal } from "react-bootstrap";
import Form from "react-bootstrap/Form";
import { translateGeneral } from "utils/localizations";
import BulkExemptionsViewModel from "viewModels/callCenter/actions/bulkExemptionsViewModel";
import BulkExemptionsFormView from "./bulkExemptionsFormView";
import BulkExemptionsPreviewView from "./bulkExemptionsPreviewView";
import AlertWithIcon from "views/common/alertWithIcon";

// eslint-disable-next-line sonarjs/cognitive-complexity
const BulkExemptionsView: ViewComponent<BulkExemptionsViewModel> = observer(({ vm }) => {
  if (vm.error) {
    return (
      <Modal show={true} onHide={vm.onClose} dialogClassName="modal-md" onEscapeKeyDown={vm.onClose} centered>
        <Modal.Body>
          <div className="py-4 text-center">
            <Icon className="mb-4 text-danger" icon={IconType.Cross} width={72} height={72} />
            <h4 className="mb-4">{vm.translate("error")}</h4>
            <p className="mb-4">{vm.error}</p>
            <Button size="sm" onClick={vm.onTryAgainClick}>
              {vm.translate("tryAgain")}
            </Button>
          </div>
        </Modal.Body>
      </Modal>
    );
  }

  if (vm.fileResponse) {
    return (
      <Modal
        show={true}
        onHide={vm.onClose}
        dialogClassName="modal-md"
        centered
        onEscapeKeyDown={(e: KeyboardEvent) => {
          e.preventDefault();
        }}>
        <Modal.Body>
          <div className="py-4 text-center">
            <Icon className="mb-4 text-primary" icon={IconType.CircleCheck} width={72} height={72} />
            <h4 className="mb-4">{vm.isCancelation ? vm.translate("cancelationComplete") : vm.translate("creationComplete")}</h4>
            <p className="mb-4">{vm.translate("description")}</p>
            <Button size="sm" onClick={vm.onFileDownloadClick}>
              {vm.translate("download")}
            </Button>
          </div>
        </Modal.Body>
      </Modal>
    );
  }

  if (vm.loading) {
    return (
      <Modal
        show={true}
        onHide={vm.onClose}
        centered
        dialogClassName="modal-md"
        onEscapeKeyDown={(e: KeyboardEvent) => {
          e.preventDefault();
        }}>
        <Modal.Body>
          <div className="py-5 text-center">
            <div className="d-flex justify-content-center my-4">
              <Loader />
            </div>
            <span className="font-weight-bold text-uppercase">{vm.translate("uploading")}</span>
          </div>
        </Modal.Body>
      </Modal>
    );
  }

  return (
    <Modal
      show={true}
      onHide={vm.onClose}
      dialogClassName={vm.bulkExemptionStep === 2 ? "modal-65w" : "modal-md"}
      centered
      className="">
      <Modal.Header closeButton={true}>
        <Modal.Title>{vm.isCancelation ? vm.translate("cancelationTitle") : vm.translate("creationTitle")}</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {vm.bulkExemptionStep === 1 && (
          <nav className="mb-4 full-width nav nav-tabs">
            <a
              href={`#/${vm.getNavigationPath().path}`}
              onClick={(e: any) => {
                e.preventDefault();
                vm.setCancelation(false);
              }}
              className={classNames("nav-item", "nav-link", {
                active: !vm.isCancelation,
              })}>
              {vm.translate("creation")}
            </a>
            <a
              href={`#/${vm.getNavigationPath().path}`}
              onClick={(e: any) => {
                e.preventDefault();
                vm.setCancelation(true);
              }}
              className={classNames("nav-item", "nav-link", {
                active: vm.isCancelation,
              })}>
              {vm.translate("cancelation")}
            </a>
          </nav>
        )}
        <Form onSubmit={preventDefault(vm.onSubmit)}>
          {vm.bulkExemptionStep === 1 && <BulkExemptionsFormView vm={vm} />}
          {vm.bulkExemptionStep === 2 && <BulkExemptionsPreviewView vm={vm} />}
          {vm.bulkExemptionStep === 1 && (
            <div className="pb-6">
              <span style={{ fontSize: "1.2rem" }}>
                {vm.translate("templateInfo")}
                <a
                  className="text-success"
                  style={{ color: "text-success" }}
                  href="https://edalnice.gov.cz/"
                  rel="noreferrer"
                  target="_blank">
                  {vm.translate("templateInfoLink")}
                </a>
              </span>
            </div>
          )}
          {vm.bulkExemptionStep !== 1 && vm.previewResponse?.rejectionReason && (
            <AlertWithIcon variant="danger">{vm.translate("warningBypassingForbidden")}</AlertWithIcon>
          )}
          <ButtonToolbar className="justify-content-center">
            {vm.bulkExemptionStep === 1 && (
              <Button size="sm" variant="light" onClick={vm.onClose}>
                {translateGeneral("cancel")}
              </Button>
            )}
            {vm.bulkExemptionStep !== 1 && (
              <Button size="sm" variant="light" onClick={vm.onBack}>
                {translateGeneral("back")}
              </Button>
            )}
            {vm.bulkExemptionStep === 1 && (
              <Button size="sm" type="submit" variant="primary">
                {translateGeneral("continue")}
              </Button>
            )}
            {vm.bulkExemptionStep !== 1 && (
              <Button size="sm" type="submit" variant="primary" disabled={vm.previewResponse?.canBeCreated === false}>
                {translateGeneral("confirm")}
              </Button>
            )}
          </ButtonToolbar>
        </Form>
      </Modal.Body>
    </Modal>
  );
});

registerView(BulkExemptionsView, BulkExemptionsViewModel);
